import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { fmtPrice, fmtUsd, fmtDollar } from '../utils/fmtPrice';
import './HlMarketSearch.css';

const API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_URL) ||
  (process.env.REACT_APP_API_URL
    ? `${String(process.env.REACT_APP_API_URL).replace(/\/$/, '')}/api`
    : 'https://trading.omniscalping.com/api');

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('sessionToken')}`,
});

const MARKETS_PAGE_SIZE = 300;

const CATEGORY_OPTIONS = [
  { id: 'all', label: 'All' },
  { id: 'crypto', label: 'Crypto' },
  { id: 'stock', label: 'Stocks' },
  { id: 'index', label: 'Indices & FX' },
  { id: 'commodity', label: 'Commodities' },
];

const categoryLabel = (cat) => {
  if (cat === 'crypto') return 'Crypto';
  if (cat === 'fx') return 'FX';
  if (cat === 'index') return 'Index';
  if (cat === 'commodity') return 'Commodity';
  return 'Stock';
};

const priceUnitLabel = (cat) => {
  if (cat === 'stock') return 'per share';
  if (cat === 'commodity') return 'per unit';
  if (cat === 'fx') return 'per unit';
  return 'market price';
};

const marketKey = (m) => m.coin || `${m.dex}:${m.symbol}`;

const formatDexLabel = (m) => String(m?.dex || 'main').toLowerCase();

export default function HlMarketSearch({
  defaultSizeUsd = 5000,
  onSelectSymbol,
  onQuickTrade,
  busy = false,
  marketsApiPrefix = 'multi-asset',
  compact = false,
  layout = 'default',
  selectedSymbolKey = null,
  includeCrypto = false,
  visibleMarketRows = null,
  dollarPrices = false,
  minDeployUsd = 100,
}) {
  const fmtMid = dollarPrices ? fmtDollar : fmtPrice;
  const listScrollStyle =
    visibleMarketRows != null && visibleMarketRows > 0
      ? { maxHeight: `${36 + visibleMarketRows * 48}px` }
      : undefined;
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('all');
  const [markets, setMarkets] = useState([]);
  const [totalListed, setTotalListed] = useState(null);
  const [totalInCategory, setTotalInCategory] = useState(null);
  const [totalMatching, setTotalMatching] = useState(null);
  const [dexes, setDexes] = useState(['xyz', 'flx', 'km']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [sizeUsd, setSizeUsd] = useState(String(defaultSizeUsd));
  const [leverage, setLeverage] = useState(1);

  useEffect(() => {
    setSizeUsd(String(defaultSizeUsd));
  }, [defaultSizeUsd]);

  const fetchMarkets = useCallback(async (q, cat) => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await axios.get(`${API_BASE}/${marketsApiPrefix}/markets`, {
        headers: authHeaders(),
        params: { q, category: cat, limit: MARKETS_PAGE_SIZE, includeCrypto: includeCrypto ? 1 : undefined },
      });
      setMarkets(data.markets || []);
      setSelected((prev) => {
        if (!prev) return prev;
        const updated = (data.markets || []).find((m) => marketKey(m) === marketKey(prev));
        return updated ? { ...prev, ...updated } : prev;
      });
      setTotalListed(data.totalListed ?? null);
      setTotalInCategory(data.totalInCategory ?? null);
      setTotalMatching(data.totalMatching ?? null);
      setDexes(data.dexes || ['xyz', 'flx', 'km']);
    } catch (e) {
      setMarkets([]);
      setTotalInCategory(null);
      setTotalMatching(null);
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, [marketsApiPrefix, includeCrypto]);

  useEffect(() => {
    const t = setTimeout(() => fetchMarkets(query, category), 300);
    return () => clearTimeout(t);
  }, [query, category, fetchMarkets]);

  const pickMarket = (m) => {
    setSelected(m);
    setLeverage(1);
    onSelectSymbol?.(m);
    if (layout === 'footer') return;
    axios
      .get(`${API_BASE}/${marketsApiPrefix}/market-meta/${m.symbol}`, {
        headers: authHeaders(),
        params: { dex: m.dex },
      })
      .then(({ data }) => {
        setSelected((prev) =>
          prev && marketKey(prev) === marketKey(m)
            ? { ...prev, ...data, mid: data.mid ?? prev.mid, maxLeverage: data.maxLeverage ?? prev.maxLeverage }
            : prev
        );
      })
      .catch(() => {});
  };

  const priceLabel = selected?.mid != null ? fmtMid(selected.mid) : null;
  const unitLabel = selected ? priceUnitLabel(selected.category) : '';

  const tradePayload = (side) => {
    if (!selected) return null;
    return {
      symbol: selected.symbol,
      hlDex: selected.dex,
      hlCoin: selected.coin,
      leverage: Number(leverage) || 1,
      side,
      action: 'open',
      sizeUsd: Number(sizeUsd) || defaultSizeUsd,
      reason: `Manual ${side} ${selected.displayName || selected.symbol} (${selected.dex}) via HL market search`,
    };
  };

  const queueSide = (side) => {
    if (!selected || !onQuickTrade) return;
    const payload = tradePayload(side);
    if (payload) onQuickTrade(payload);
  };

  const dexLabel = dexes.join(', ');

  return (
    <section className={`hl-market-search ${layout === 'footer' ? 'hl-market-footer' : ''} ${compact ? 'hl-market-compact' : ''}`}>
      <div className="hl-market-search-header">
        <h2>Find a market on Hyperliquid</h2>
        <p className="hl-market-search-desc">
          {includeCrypto ? (
            <>
              Main Hyperliquid perps (<strong>BTC, ETH, SOL</strong>, …) plus Tradfi on{' '}
              <strong>xyz</strong> (USDC), <strong>flx</strong> and <strong>km</strong> (USDH). Pick the dex
              tag for Tradfi rows; crypto uses the main HL book.
            </>
          ) : (
            <>
              Same Tradfi universe as the HL app: <strong>xyz</strong> (USDC), <strong>flx</strong> and{' '}
              <strong>km</strong> (USDH). Some symbols (e.g. GOLD, SILVER) appear on more than one venue with
              different prices — pick the row with the dex tag you want.
            </>
          )}
        </p>
      </div>

      <div className="hl-market-search-controls">
        <input
          type="search"
          className="hl-market-search-input"
          placeholder="Search e.g. GOLD, USOIL, WTIOIL, SPX…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
        <div className="hl-market-category-tabs">
          {CATEGORY_OPTIONS.map((c) => (
            <button
              key={c.id}
              type="button"
              className={`hl-market-cat-btn ${category === c.id ? 'active' : ''}`}
              onClick={() => setCategory(c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="hl-market-search-error">{error}</p>}
      {loading && <p className="hl-market-search-hint">Loading markets…</p>}
      {!loading && (totalInCategory != null || totalListed != null) && (
        <p className="hl-market-search-hint">
          Showing {markets.length} of{' '}
          {query && totalMatching != null
            ? totalMatching
            : totalInCategory != null
              ? totalInCategory
              : markets.length}
          {query ? ` matching “${query}”` : ''} in{' '}
          {CATEGORY_OPTIONS.find((c) => c.id === category)?.label || category}
          {visibleMarketRows != null && visibleMarketRows > 0 && markets.length > visibleMarketRows
            ? ` · scroll list for all ${markets.length}`
            : ''}
          {totalInCategory != null && totalInCategory > MARKETS_PAGE_SIZE && !query
            ? ` (first ${MARKETS_PAGE_SIZE})`
            : ''}
          {totalListed != null ? ` · ${totalListed} total Tradfi (${dexLabel})` : ''}
        </p>
      )}

      <ul
        className={`hl-market-results${visibleMarketRows != null ? ' hl-market-results-scroll' : ''}`}
        style={listScrollStyle}
      >
        {markets.map((m) => (
          <li key={marketKey(m)}>
            <button
              type="button"
              className={`hl-market-row ${(selected && marketKey(selected) === marketKey(m)) || (selectedSymbolKey && marketKey(m) === selectedSymbolKey) ? 'selected' : ''}`}
              onClick={() => pickMarket(m)}
            >
              <span className="hl-market-symbol">{m.displayName || m.symbol}</span>
              <span className={`hl-market-dex dex-${formatDexLabel(m)}`}>{formatDexLabel(m)}</span>
              <span className="hl-market-cat">{categoryLabel(m.category)}</span>
              <span className="hl-market-max-lev">{m.maxLeverage != null ? `${m.maxLeverage}x` : '—'}</span>
              <span className="hl-market-mid">{m.mid != null ? fmtMid(m.mid) : '—'}</span>
            </button>
          </li>
        ))}
        {!loading && !markets.length && (
          <li className="hl-market-empty">No markets match. Try another symbol or category.</li>
        )}
      </ul>

      {selected && layout !== 'footer' && (
        <div className="hl-market-selected">
          <div className="hl-market-selected-head">
            <strong>{selected.displayName || selected.symbol}</strong>
            <span className="hl-market-dex">{selected.dex}</span>
            <span className="hl-market-cat">{categoryLabel(selected.category)}</span>
            {selected.mid != null && <span className="hl-market-mid">Mid {fmtMid(selected.mid)}</span>}
            {selected.maxLeverage != null && (
              <span className="hl-market-lev-hint">HL max {selected.maxLeverage}x</span>
            )}
          </div>
          <div className="hl-market-quick-form">
            <label>
              Leverage (max {selected.maxLeverage ?? '—'}x)
              <select
                value={String(leverage)}
                onChange={(e) => setLeverage(Number(e.target.value))}
              >
                {Array.from(
                  { length: Math.max(1, Math.min(50, selected.maxLeverage || 1)) },
                  (_, i) => i + 1
                ).map((x) => (
                  <option key={x} value={x}>
                    {x}x
                  </option>
                ))}
              </select>
            </label>
            <label>
              Deploy (USD margin)
              <input
                type="number"
                min={minDeployUsd}
                step={1}
                value={sizeUsd}
                onChange={(e) => setSizeUsd(e.target.value)}
              />
            </label>
            {priceLabel && (
              <p className="hl-market-price-note">
                Market price: <strong>{priceLabel}</strong> {unitLabel}. Orders fill at market — long near the
                ask, short near the bid (not a limit order).
              </p>
            )}
            <div className="hl-market-quick-actions">
              <button
                type="button"
                className="primary-btn"
                disabled={busy}
                onClick={() => queueSide('long')}
              >
                {busy
                  ? '…'
                  : `Open long · ${fmtUsd(Number(sizeUsd) || defaultSizeUsd)}${priceLabel ? ` @ ~${priceLabel}` : ''}`}
              </button>
              <button
                type="button"
                className="secondary-btn hl-short-btn"
                disabled={busy}
                onClick={() => queueSide('short')}
              >
                {busy
                  ? '…'
                  : `Open short · ${fmtUsd(Number(sizeUsd) || defaultSizeUsd)}${priceLabel ? ` @ ~${priceLabel}` : ''}`}
              </button>
              <button
                type="button"
                className="secondary-btn"
                disabled={busy}
                onClick={() => onSelectSymbol?.(selected)}
              >
                Load in trade panel
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
